/* DepositCreditList.js — وصول سپرده (بستانکاری) */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar, useTabs } from '../TabManager';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';
import CreditDetailList from './CreditDetailList';
import { FromDocumentPanel, UpLevelPanel } from './CreditTools';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

/* ── کمکی‌ها ── */
const toFa   = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => { const v=Number(n||0); return v===0?'':v.toLocaleString('fa-IR'); };
const API    = (path,opts={}) => fetch(`/api/deposit${path}`,{
  headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts,
}).then(jsonOf, netErr);

const MONTHS = ['فروردین','اردیبهشت','خرداد','تیر','مرداد','شهریور',
                'مهر','آبان','آذر','دی','بهمن','اسفند'];

/* زیرکامپوننت‌ها بیرون — وگرنه فوکوس می‌پره */
const FInput = ({label,name,value,onChange,readOnly,flex=1,type='text',dir}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <input name={name} type={type} value={value??''} onChange={onChange} readOnly={readOnly}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',
        direction:dir||'rtl',textAlign:dir==='ltr'?'left':'right',
        width:'100%',boxSizing:'border-box',background:readOnly?'#f5f5f5':'#fff'}}/>
  </div>
);

const FSelect = ({label,name,value,onChange,options,flex=1}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <select name={name} value={value??''} onChange={onChange}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',
        width:'100%',boxSizing:'border-box',background:'#fff'}}>
      {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

/* ستون‌های گرید اصلی */
const INIT_WIDTHS = [45,85,240,120,110,60,80,90,90,90,70,130,130,120,130,130,120,120,110,100,200];

const EMPTY = {credit_no:'',credit_year:'',credit_month:'',kind_code:1,deposit_amount:'',
  department_id:0,department:'',letter_no:'',letter_date:'',remark:'',
  up_credit_no:'',up_credit_year:''};

export default function DepositCreditList({ user, onBack }) {
  // دکمه‌های فرم FRMCREDITNEW — هر دکمه فقط با مجوز خودش دیده می‌شود
  const vbf = useFormButtons('FRMCREDITNEW', ["BTNDETAIL","BTNNEW","BTNEDIT","BTNDELETE","BTNUPLEVELCREDITNO","BTNOK"]);
  /* ── گرید اصلی ── */
  const [rows,setRows]           = useState([]);
  const [total,setTotal]         = useState(0);
  const [sumAmount,setSumAmount] = useState(0);
  const [hasMore,setHasMore]     = useState(false);
  const [loading,setLoading]     = useState(false);
  const [selected,setSelected]   = useState(null);
  const [tool,setTool]           = useState(null);   // 'fromdoc' | 'uplevel'

  /* ── فیلترها ── */
  const [year,setYear]     = useState(0);
  const [month,setMonth]   = useState(0);
  const [kind,setKind]     = useState(0);
  const [dept,setDept]     = useState(0);
  const [years,setYears]   = useState([]);
  const [depts,setDepts]   = useState([]);
  const [ready,setReady]   = useState(false);

  /* ── زیرگرید ریز سپرده ── */
  const [showDetail,setShowDetail] = useState(false);
  const [details,setDetails]       = useState([]);
  const [sumDetail,setSumDetail]   = useState(0);
  const [sumRefund,setSumRefund]   = useState(0);

  /* ── مودال‌ها ── */
  const [modal,setModal] = useState(null);      // new | edit | delete
  const [form,setForm]   = useState(EMPTY);
  const [error,setError] = useState('');
  const [toast,setToast] = useState('');
  const [lookup,setLookup] = useState([]);

  const offsetRef = useRef(0);
  const {openTab, goHome} = useTabs();

  const {TH} = useColumnResize(INIT_WIDTHS);
  const {visibleRows,search,setSearch,totalCount,LoadMoreButton} = useTablePagination({
    allRows:rows, searchFields:['remark','letter_no','credit_no','department'],
    serverTotal:total, serverHasMore:hasMore,
  });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),2800); };

  /* مقداردهی اولیه */
  useEffect(()=>{ (async()=>{
    try{
      const d = await API('/credits/init/');
      if(d.ok){
        setYear(d.year||0); setMonth(d.month||0);
        setYears(d.years||[]); setDepts(d.departments||[]);
        if(d.warning) alert(d.warning);
      }
    }catch(e){console.error(e);}
    setReady(true);
  })(); },[]);

  const fetchData = useCallback(async(reset=false)=>{
    if(reset){ offsetRef.current=0; setRows([]); setSelected(null); setDetails([]); }
    setLoading(true);
    try{
      const off = reset?0:offsetRef.current;
      const d = await API(`/credits/?offset=${off}&year=${year}&month=${month}&kind=${kind}&department_id=${dept}`);
      if(d.ok){
        setRows(p=>reset?d.rows:[...p,...d.rows]);
        setTotal(d.total); setSumAmount(d.sum_amount); setHasMore(d.has_more);
        offsetRef.current = (reset?0:offsetRef.current)+d.rows.length;
      }
    }catch(e){console.error(e);}
    setLoading(false);
  },[year,month,kind,dept]);

  useEffect(()=>{ if(ready) fetchData(true); },[ready,year,month,kind,dept]); // eslint-disable-line

  /* انتخاب ردیف → بارگذاری ریز سپرده */
  const selectRow = async r => {
    setSelected(r);
    if(!showDetail || r.detail_count===0){ setDetails([]); setSumDetail(0); setSumRefund(0); return; }
    const d = await API(`/credits/${r.id}/details/`);
    if(d.ok){ setDetails(d.rows); setSumDetail(d.sum_detail); setSumRefund(d.sum_refund); }
  };
  useEffect(()=>{ if(selected) selectRow(selected); },[showDetail]); // eslint-disable-line

  const handleChange = e => { const{name,value}=e.target; setForm(p=>({...p,[name]:value})); };

  /* باز کردن صفحه ریز سپرده در تب جدید */
  const openRows = ()=>{ if (!vbf.can('BTNDETAIL')) return;
    if(!selected) return;
    openTab(`credit:rows:${selected.id}`,
      `ریز سپرده ${selected.credit_no}/${selected.credit_year}`, '📄',
      () => <CreditDetailList user={user} credit={selected} onBack={goHome}/>);
  };

  /* ── جدید / ویرایش ── */
  const openNew = ()=>{ if (!vbf.can('BTNNEW')) return;
    setForm({...EMPTY, credit_year:year||'', credit_month:month||'',
      department_id:dept||0, department:depts.find(d=>d.id===dept)?.title||''});
    setError(''); setLookup([]); setModal('new');
  };
  const openEdit = ()=>{ if (!vbf.can('BTNEDIT')) return;
    if(!selected) return;
    setForm({credit_no:selected.credit_no, credit_year:selected.credit_year,
      credit_month:selected.credit_month, kind_code:selected.kind_code||1,
      deposit_amount:selected.deposit_amount, department_id:selected.department_id,
      department:selected.department, letter_no:selected.letter_no,
      letter_date:selected.letter_date, remark:selected.remark,
      up_credit_no:selected.up_credit_no, up_credit_year:selected.up_credit_year});
    setError(''); setLookup([]); setModal('edit');
  };

  /* اعتبارسنجی سمت کلاینت */
  const validate = f => {
    if(Number(f.credit_no||0)===0)   return 'شماره بستانکاری وارد نشده است';
    if(Number(f.credit_year||0)===0) return 'سال بستانکاری وارد نشده است';
    return null;
  };

  const handleSave = async()=>{
    const v = validate(form);
    if(v){ setError(v); return; }
    setError('');
    const url = modal==='new' ? '/credits/create/' : `/credits/${selected.id}/update/`;
    const d = await API(url,{method:'POST',body:JSON.stringify(form)});
    if(d.ok){
      setModal(null); fetchData(true);
      showToast(modal==='new'?'✅ ثبت شد':'✅ ویرایش شد');
    } else setError(d.error||'خطا');
  };

  /* ── حذف — منع در صورت وجود ریز سپرده ── */
  const openDelete = ()=>{ if (!vbf.can('BTNDELETE')) return;
    if(!selected) return;
    setError(''); setModal('delete');
  };
  const handleDelete = async()=>{
    const d = await API(`/credits/${selected.id}/delete/`,{method:'POST'});
    if(d.ok){ setModal(null); setSelected(null); fetchData(true); showToast('✅ حذف شد'); }
    else setError(d.error||'خطا');
  };

  const doLookup = async q => {
    const d = await API(`/departments/lookup/?q=${encodeURIComponent(q||'')}`);
    if(d.ok) setLookup(d.rows);
  };

  const monthOpts = [{value:0,label:'همه ماه‌ها'},
    ...MONTHS.map((m,n)=>({value:n+1,label:m}))];
  const yearOpts  = [{value:0,label:'همه سال‌ها'},
    ...years.map(y=>({value:y,label:toFa(y)}))];
  const kindOpts  = [{value:0,label:'همه انواع'},
    {value:1,label:'هزینه ای'},{value:2,label:'تملک داراییهای سرمایه ای'}];
  const deptOpts  = [{value:0,label:'همه دستگاه‌ها'},
    ...depts.map(d=>({value:d.id,label:d.title}))];

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.hLeft}>
          <button onClick={onBack} style={s.backBtn}>← بازگشت</button>
          <div style={s.hLogo}><img src={require('../../assets/images/arjan.png')} alt="" style={{width:'34px',height:'34px',borderRadius:'7px'}}/></div>
          <div><div style={s.hTitle}>وصول سپرده</div><div style={s.hSub}>بستانکاری — بخش سپرده</div></div>
        </div>
        <span style={s.userName}>{user?.full_name}</span>
      </header>
      <TabBar/>

      <main style={s.main}>
        {/* ── نوار ابزار ── */}
        <div style={s.toolbar}>
          <div style={s.toolBtns}>
            {vbf.can('BTNNEW') && <button onClick={openNew} style={s.btnNew}>＋ جدید</button>}
            {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{...s.btnEdit,opacity:selected?1:.4}}>✎ ویرایش</button>}
            {vbf.can('BTNDELETE') && <button onClick={openDelete} disabled={!selected} style={{...s.btnDel,opacity:selected?1:.4}}>✕ حذف</button>}
            {vbf.can('BTNDETAIL') && <button onClick={openRows} disabled={!selected}
              style={{...s.btnDetail,opacity:selected?1:.4}}>
              📄 ریز سپرده{selected?.detail_count?` (${toFa(selected.detail_count)})`:''}</button>}
            {/* دریافت از تنظیم حساب (مجوز BTNNEW) و تعیین بستانکاری خزانه — روی بستانکاری انتخاب‌شده */}
            {vbf.can('BTNNEW') && <button onClick={()=>selected&&setTool('fromdoc')} disabled={!selected}
              style={{...s.btnDetail,opacity:selected?1:.4}}>🧾 دريافت اطلاعات از تنظیم حساب</button>}
            {vbf.can('BTNUPLEVELCREDITNO') && <button onClick={()=>selected&&setTool('uplevel')} disabled={!selected}
              style={{...s.btnDetail,opacity:selected?1:.4}}>🏛 تعیین بستانکاری خزانه</button>}
          </div>
          <label style={{...s.check,marginRight:'auto'}}>
            <input type="checkbox" checked={showDetail} onChange={e=>setShowDetail(e.target.checked)}/>
            <span>نمایش ریز سپرده</span>
          </label>
        </div>

        {/* ── فیلترها + جستجو ── */}
        <div style={s.toolbar2}>
          <div style={{width:'130px'}}>
            <FSelect label="سال" name="year" value={year}
              onChange={e=>setYear(Number(e.target.value))} options={yearOpts}/>
          </div>
          <div style={{width:'140px'}}>
            <FSelect label="ماه" name="month" value={month}
              onChange={e=>setMonth(Number(e.target.value))} options={monthOpts}/>
          </div>
          <div style={{width:'210px'}}>
            <FSelect label="نوع سپرده" name="kind" value={kind}
              onChange={e=>setKind(Number(e.target.value))} options={kindOpts}/>
          </div>
          <div style={{width:'260px'}}>
            <FSelect label="دستگاه اجرایی" name="dept" value={dept}
              onChange={e=>setDept(Number(e.target.value))} options={deptOpts}/>
          </div>
          <div style={{marginRight:'auto'}}>
            <div style={s.fL}>جستجو</div>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="ملاحظات / شماره نامه / شماره بستانکاری" style={{...s.fI,width:'280px'}}/>
          </div>
        </div>

        {/* ── گرید اصلی ── */}
        <div style={s.tableWrap}>
          <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
            <thead style={{position:'sticky',top:0,zIndex:3}}>
              <tr>
                <TH idx={0}>ردیف</TH>
                <TH idx={1}>کد دستگاه</TH>
                <TH idx={2} right>دستگاه</TH>
                <TH idx={3}>شناسه ملی</TH>
                <TH idx={4}>نوع</TH>
                <TH idx={5}>سال</TH>
                <TH idx={6}>ماه</TH>
                <TH idx={7}>بستانکاری</TH>
                <TH idx={8}>بس خزانه</TH>
                <TH idx={9}>سال خزانه</TH>
                <TH idx={10}>تعداد</TH>
                <TH idx={11} gold>مبلغ بستانکاری</TH>
                <TH idx={12} gold>وارد شده</TH>
                <TH idx={13} gold>وارد نشده</TH>
                <TH idx={14} red>استرداد شده</TH>
                <TH idx={15} red>استرداد نشده</TH>
                <TH idx={16} green>دارای سند</TH>
                <TH idx={17} green>بدون سند</TH>
                <TH idx={18}>شماره</TH>
                <TH idx={19}>تاریخ</TH>
                <TH idx={20} right>ملاحظات</TH>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length===0 && (
                <tr><td colSpan={21} style={s.empty}>
                  {loading?'در حال بارگذاری...':'اطلاعاتی برای نمایش وجود ندارد'}
                </td></tr>)}
              {visibleRows.map((r,idx)=>{
                const sel = selected?.id===r.id;
                const bg  = sel ? '#64b5f6'
                          : r.not_entered!==0 ? '#fff8e1'
                          : idx%2===0 ? '#fff' : '#fdf6e3';
                return (
                  <tr key={r.id} onClick={()=>selectRow(r)} style={{cursor:'pointer',background:bg}}
                    onMouseEnter={e=>{if(!sel)e.currentTarget.style.background='#bbdefb';}}
                    onMouseLeave={e=>{if(!sel)e.currentTarget.style.background=bg;}}>
                    <td style={s.td}>{toFa(idx+1)}</td>
                    <td style={s.td}>{toFa(r.dept_code)}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.department}>{r.department}</td>
                    <td style={{...s.td,direction:'ltr',fontSize:'11px'}}>{toFa(r.national_id)}</td>
                    <td style={s.td}>{r.kind_code===2?'تملک':r.kind_code===1?'هزینه':''}</td>
                    <td style={s.td}>{toFa(r.credit_year)}</td>
                    <td style={s.td}>{toFa(r.credit_month)}</td>
                    <td style={{...s.td,fontWeight:'700'}}>{toFa(r.credit_no)}</td>
                    <td style={s.td}>{r.up_credit_no?toFa(r.up_credit_no):''}</td>
                    <td style={s.td}>{r.up_credit_year?toFa(r.up_credit_year):''}</td>
                    <td style={s.td}>{toFa(r.detail_count)}</td>
                    <td style={{...s.td,...s.num,color:'#1b5e20'}}>{numFmt(r.deposit_amount)}</td>
                    <td style={{...s.td,...s.num,color:'#1565c0'}}>{numFmt(r.entered)}</td>
                    <td style={{...s.td,...s.num,color:r.not_entered!==0?'#e65100':'#999'}}>{numFmt(r.not_entered)}</td>
                    <td style={{...s.td,...s.num,color:'#6a1b9a'}}>{numFmt(r.refunded)}</td>
                    <td style={{...s.td,...s.num}}>{numFmt(r.not_refunded)}</td>
                    <td style={{...s.td,...s.num,color:'#00695c'}}>{numFmt(r.with_document)}</td>
                    <td style={{...s.td,...s.num,color:'#8d6e63'}}>{numFmt(r.without_document)}</td>
                    <td style={s.td}>{toFa(r.letter_no)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.letter_date)}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.remark}>{r.remark}</td>
                  </tr>);
              })}
            </tbody>
          </table>
        </div>
        <LoadMoreButton onClick={()=>fetchData(false)} loading={loading}/>

        {/* ── نوار جمع‌ها ── */}
        <div style={s.summary}>
          <span>تعداد: <b>{toFa(visibleRows.length)}</b> از <b>{toFa(totalCount)}</b></span>
          <span>جمع مبلغ بستانکاری: <b style={{color:'#1b5e20'}}>{numFmt(sumAmount)||'۰'}</b> ریال</span>
          {selected && <>
            <span>ردیف انتخابی: بستانکاری <b>{toFa(selected.credit_no)}</b>/<b>{toFa(selected.credit_year)}</b></span>
            <span>تعداد ریز: <b>{toFa(selected.detail_count)}</b></span>
            <span>وارد نشده: <b style={{color:selected.not_entered!==0?'#e65100':'#1b5e20'}}>{numFmt(selected.not_entered)||'۰'}</b></span>
          </>}
        </div>

        {/* ── زیرگرید ریز سپرده ── */}
        {showDetail && details.length>0 && (
          <div style={s.subWrap}>
            <div style={{...s.subTitle,background:'linear-gradient(135deg,#0d2137,#1a2f50)',color:'#C8A84B'}}>
              📄 ریز سپرده — جمع: {numFmt(sumDetail)} ریال | جمع استرداد: {numFmt(sumRefund)||'۰'} ریال
            </div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>
                {['ردیف','ردیف خزانه','مبلغ سپرده','استرداد','مانده','موضوع سپرده','پیمانکار',
                  'قرارداد','تاریخ قرارداد','قبض','تاریخ قبض','شماره فیش','ملاحظات','منع استرداد']
                  .map(h=><th key={h} style={s.subTh}>{h}</th>)}
              </tr></thead>
              <tbody>
                {details.map((r,i)=>(
                  <tr key={r.id} style={{background:i%2===0?'#fff':'#fffde7'}}>
                    <td style={s.subTd}>{toFa(r.detail_row)}</td>
                    <td style={s.subTd}>{r.up_detail_row?toFa(r.up_detail_row):''}</td>
                    <td style={{...s.subTd,...s.num,color:'#1b5e20'}}>{numFmt(r.amount)}</td>
                    <td style={{...s.subTd,...s.num,color:'#6a1b9a'}}>{numFmt(r.refund_pay)}</td>
                    <td style={{...s.subTd,...s.num}}>{numFmt(r.remain)}</td>
                    <td style={{...s.subTd,textAlign:'right'}}>{r.subject}</td>
                    <td style={{...s.subTd,textAlign:'right'}}>{r.contractor}</td>
                    <td style={s.subTd}>{toFa(r.contract_no)}</td>
                    <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.contract_date)}</td>
                    <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.slip_no)}</td>
                    <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.slip_date)}</td>
                    <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.receipt_no)}</td>
                    <td style={{...s.subTd,textAlign:'right'}} title={r.remark}>{r.remark}</td>
                    <td style={{...s.subTd,color:'#c62828',fontWeight:'700'}}>
                      {r.refund_blocked?(r.block_reason||'بلی'):''}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* ══ مودال جدید / ویرایش ══ */}
      {(modal==='new'||modal==='edit') && (
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.mHeader}>
              <div style={s.mTitle}>{modal==='new'?'➕ ثبت بستانکاری جدید':'✎ ویرایش بستانکاری'}</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <div style={s.row}>
                <FInput label="شماره بستانکاری *" name="credit_no" value={form.credit_no}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 24%"/>
                <FInput label="سال *" name="credit_year" value={form.credit_year}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 18%"/>
                <FInput label="ماه" name="credit_month" value={form.credit_month}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 14%"/>
                <FSelect label="نوع سپرده" name="kind_code" value={form.kind_code}
                  onChange={e=>setForm(p=>({...p,kind_code:Number(e.target.value)}))}
                  options={[{value:1,label:'هزینه ای'},{value:2,label:'تملک داراییهای سرمایه ای'}]}
                  flex="0 0 40%"/>
              </div>
              <div style={s.row}>
                <FInput label="مبلغ سپرده" name="deposit_amount" value={form.deposit_amount}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 40%"/>
                <div style={{flex:'0 0 58%',display:'flex',flexDirection:'column',gap:'4px',position:'relative'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>واحد اجرایی</label>
                  <input value={form.department} placeholder="تایپ کنید..."
                    onChange={e=>{setForm(p=>({...p,department:e.target.value,department_id:0}));doLookup(e.target.value);}}
                    onFocus={()=>doLookup(form.department)} style={s.fI}/>
                  {lookup.length>0&&(
                    <div style={s.dropdown}>{lookup.map(r=>(
                      <div key={r.id} onClick={()=>{setForm(p=>({...p,department_id:r.id,department:r.title}));setLookup([]);}}
                        style={s.dropItem}>{toFa(r.code)} — {r.title}</div>))}</div>)}
                </div>
              </div>
              <div style={s.row}>
                <FInput label="دبیرخانه: شماره" name="letter_no" value={form.letter_no}
                  onChange={handleChange} flex="0 0 32%"/>
                <div style={{flex:'0 0 32%',display:'flex',flexDirection:'column',gap:'4px'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>تاریخ</label>
                  <JalaliDatePicker value={form.letter_date}
                    onChange={v=>setForm(p=>({...p,letter_date:v}))} placeholder="تاریخ نامه"/>
                </div>
              </div>
              <div style={s.row}>
                <FInput label="بستانکاری خزانه: شماره" name="up_credit_no" value={form.up_credit_no}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 32%"/>
                <FInput label="سال خزانه" name="up_credit_year" value={form.up_credit_year}
                  onChange={handleChange} type="number" dir="ltr" flex="0 0 32%"/>
              </div>
              <div style={s.row}>
                <FInput label="ملاحظات" name="remark" value={form.remark} onChange={handleChange}/>
              </div>
              {modal==='edit'&&selected?.detail_count>0&&(
                <div style={s.warnBox}>
                  ⚠️ این بستانکاری {toFa(selected.detail_count)} ریز سپرده دارد؛
                  با تغییر شماره یا سال، ریز سپرده و ریز استرداد هم منتقل می‌شوند.
                </div>)}
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {vbf.can('BTNOK') && <button onClick={handleSave} style={s.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال حذف ══ */}
      {modal==='delete'&&selected&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'480px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={s.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <p style={{fontSize:'14px',lineHeight:'2'}}>
                آیا از حذف بستانکاری شماره <b>{toFa(selected.credit_no)}</b> سال <b>{toFa(selected.credit_year)}</b> به
                مبلغ <b>{numFmt(selected.deposit_amount)}</b> ریال مطمئن هستید؟</p>
              {selected.detail_count>0&&<p style={{color:'#c62828',fontWeight:'700'}}>
                ⚠️ برای حذف بستانکاری ابتدا ریز سپرده های آن را حذف کنید.</p>}
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {selected.detail_count===0&&(vbf.can('BTNDELETE') && <button onClick={handleDelete}
                style={{...s.btnSave,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>حذف</button>)}
            </div>
          </div>
        </div>
      )}

      {toast&&<div style={s.toast}>{toast}</div>}
      {tool==='fromdoc'&&selected&&(
        <FromDocumentPanel credit={selected} onClose={()=>setTool(null)}
          onSaved={()=>{ setTool(null); fetchData(true); }}/>)}
      {tool==='uplevel'&&selected&&(
        <UpLevelPanel credit={selected} onClose={()=>setTool(null)}
          onSaved={()=>{ setTool(null); fetchData(true); }}/>)}
    </div>
  );
}

const s = {
  page:{height:'100vh',overflow:'hidden',background:'#f0f2f7',fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:'rtl',display:'flex',flexDirection:'column'},
  header:{background:'linear-gradient(135deg,#0a1628,#1a2f50)',padding:'10px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:'0 2px 16px rgba(0,0,0,0.3)',flexShrink:0},
  hLeft:{display:'flex',alignItems:'center',gap:'12px'},
  backBtn:{padding:'6px 14px',background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  hLogo:{width:'40px',height:'40px',background:'rgba(200,168,75,0.12)',border:'1px solid rgba(200,168,75,0.3)',borderRadius:'9px',display:'flex',alignItems:'center',justifyContent:'center'},
  hTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  hSub:{fontSize:'11px',color:'rgba(255,255,255,0.55)'},
  userName:{fontSize:'13px',color:'rgba(255,255,255,0.85)'},
  main:{padding:'8px 20px',flex:1,display:'flex',flexDirection:'column',overflow:'auto',minHeight:0},
  toolbar:{display:'flex',alignItems:'center',gap:'8px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolbar2:{display:'flex',alignItems:'flex-end',gap:'10px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolBtns:{display:'flex',gap:'7px'},
  check:{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',fontWeight:'600',color:'#444',cursor:'pointer'},
  fL:{fontSize:'11px',fontWeight:'600',color:'#555',marginBottom:'3px'},
  fI:{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',boxSizing:'border-box',width:'100%'},
  btnNew:{padding:'7px 16px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnEdit:{padding:'7px 16px',background:'linear-gradient(135deg,#1a237e,#283593)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnDel:{padding:'7px 16px',background:'linear-gradient(135deg,#b71c1c,#c62828)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnDetail:{padding:'7px 16px',background:'linear-gradient(135deg,#006064,#00838f)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  tableWrap:{overflowX:'auto',overflowY:'auto',borderRadius:'10px',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',background:'#fff',flex:1,minHeight:'260px'},
  td:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  num:{direction:'ltr',textAlign:'left',fontWeight:'700'},
  empty:{textAlign:'center',padding:'40px',color:'#999',fontSize:'13px'},
  summary:{display:'flex',gap:'20px',flexWrap:'wrap',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',margin:'6px 0',fontSize:'12px',color:'#444',flexShrink:0},
  subWrap:{background:'#fff',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.1)',marginBottom:'8px',overflow:'auto',flexShrink:0},
  subTitle:{padding:'7px 14px',fontSize:'12px',fontWeight:'700'},
  subTh:{padding:'7px 9px',background:'#0d2137',color:'#C8A84B',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap',position:'sticky',top:0,zIndex:2},
  subTd:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'},
  modal:{background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'760px',maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'},
  mHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 20px',background:'linear-gradient(135deg,#0a1628,#1a2f50)',borderRadius:'16px 16px 0 0'},
  mTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  closeBtn:{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'30px',height:'30px',borderRadius:'50%',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',justifyContent:'center'},
  mBody:{overflowY:'auto',flex:1,padding:'18px'},
  mFooter:{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'12px 18px',borderTop:'1px solid #f0f2f7',background:'#f8f9fa',borderRadius:'0 0 16px 16px'},
  row:{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap'},
  errBox:{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',color:'#c62828',marginBottom:'10px',fontSize:'13px'},
  warnBox:{background:'#fff8e1',border:'1px solid #ffe082',borderRadius:'8px',padding:'9px 14px',color:'#e65100',marginBottom:'8px',fontSize:'12px'},
  dropdown:{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid #ddd',borderRadius:'8px',maxHeight:'190px',overflowY:'auto',zIndex:50,boxShadow:'0 6px 20px rgba(0,0,0,0.18)'},
  dropItem:{padding:'7px 11px',fontSize:'12px',cursor:'pointer',borderBottom:'1px solid #f5f5f5',textAlign:'right'},
  btnCancel:{padding:'8px 18px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  btnSave:{padding:'8px 22px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  toast:{position:'fixed',bottom:'24px',right:'24px',background:'#1b5e20',color:'#fff',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'600',zIndex:2000,boxShadow:'0 4px 16px rgba(0,0,0,0.25)'},
};
